import {
  CognitoException,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  initiateCustomAuth,
  normalizeEmail,
  refreshTokens,
  respondToCustomChallenge,
  signOut,
  signUp,
} from "./cognitoAuth";
import {
  createSmUser,
  fetchProfile,
  linkSmUser,
  SmApiError,
  type SmProfile,
} from "../api/smApi";

export interface AuthStatus {
  isAuthenticated: boolean;
  username?: string;
  userId?: string;
  loginId?: string | null;
  attributes?: Record<string, string>;
  profile?: SmProfile | null;
}

export interface FetchAuthStatusOptions {
  session?: Awaited<ReturnType<typeof fetchAuthSession>>;
  includeUser?: boolean;
  includeAttributes?: boolean;
  includeProfile?: boolean;
}

export interface StartLoginResult {
  normalizedEmail: string;
  nextStep: string;
  isSignedIn: boolean;
}

export interface StartSignUpResult {
  normalizedEmail: string;
  nextStep: string;
  isSignUpComplete: boolean;
}

export { normalizeEmail } from "./cognitoAuth";

export function authErrorMessage(
  error: unknown,
  fallback = "Authentication failed"
): string {
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }
  return fallback;
}

export async function fetchAuthStatus(
  options: FetchAuthStatusOptions = {},
): Promise<AuthStatus> {
  const {
    session: providedSession,
    includeUser = true,
    includeAttributes = true,
    includeProfile = true,
  } = options;
  try {
    const session = providedSession ?? (await fetchAuthSession());
    if (!session.tokens) {
      return { isAuthenticated: false };
    }
    const accessToken = session.tokens.accessToken?.toString();
    const idToken = session.tokens.idToken?.toString();
    if (!accessToken) {
      return { isAuthenticated: false };
    }

    const shouldLoadUser = includeUser || includeProfile;
    const shouldLoadAttributes = includeAttributes || includeProfile;

    const [user, userAttributes] = await Promise.all([
      shouldLoadUser ? getCurrentUser().catch(() => null) : Promise.resolve(null),
      shouldLoadAttributes
        ? fetchUserAttributes().catch(() => ({}))
        : Promise.resolve({}),
    ]);

    const attributes: Record<string, string> = {};
    if (shouldLoadAttributes) {
      // Convert attributes to Record<string, string> by filtering out undefined values
      for (const [key, value] of Object.entries(userAttributes)) {
        if (typeof value === "string") {
          attributes[key] = value;
        }
      }
    }

    let profile: SmProfile | null | undefined;
    if (includeProfile) {
      profile = user
        ? await ensureSmProfile(accessToken, idToken, attributes, user).catch(
            () => null
          )
        : null;
    }

    const status: AuthStatus = {
      isAuthenticated: true,
    };

    if (user) {
      status.username = user.username;
      status.userId = user.userId;
      status.loginId = user.signInDetails?.loginId ?? user.username;
    }

    if (shouldLoadAttributes) {
      status.attributes = attributes;
    }

    if (includeProfile) {
      status.profile = profile ?? null;
    }

    return status;
  } catch {
    return { isAuthenticated: false };
  }
}

async function ensureSmProfile(
  accessToken: string,
  idToken: string | undefined,
  attributes: Record<string, string>,
  user: Awaited<ReturnType<typeof getCurrentUser>>
): Promise<SmProfile> {
  const profileToken = idToken ?? accessToken;
  try {
    return await fetchProfile(profileToken);
  } catch (error) {
    if (!(error instanceof SmApiError)) {
      throw error;
    }
    if (error.status !== 401 && error.status !== 403) {
      throw error;
    }
  }

  const email = normalizeEmail(
    attributes.email || user.signInDetails?.loginId || user.username || ""
  );
  const firstName = (attributes.given_name || "?").trim() || "?";
  const lastName = (attributes.family_name || "?").trim() || "?";

  if (email) {
    try {
      const signupResponse = await createSmUser({
        email,
        first_name: firstName,
        last_name: lastName,
      });
      if (idToken && signupResponse.token) {
        await linkSmUser(idToken, signupResponse.token);
      }
    } catch (error) {
      console.error("Failed to create Sailmate profile", error);
    }
  }

  return fetchProfile(profileToken);
}

export async function startEmailLogin(
  email: string
): Promise<StartLoginResult> {
  const normalizedEmail = normalizeEmail(email);
  try {
    await initiateCustomAuth(normalizedEmail);
  } catch (error) {
    if (error instanceof CognitoException && error.isUserNotFound) {
      await signUp(normalizedEmail);
      await initiateCustomAuth(normalizedEmail);
    } else {
      throw error;
    }
  }

  return {
    normalizedEmail,
    nextStep: "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE",
    isSignedIn: false,
  };
}

export async function startSignUp(
  email: string,
  _firstName: string,
  _lastName: string
): Promise<StartSignUpResult> {
  const normalizedEmail = normalizeEmail(email);
  await signUp(normalizedEmail);
  await initiateCustomAuth(normalizedEmail);

  return {
    normalizedEmail,
    nextStep: "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE",
    isSignUpComplete: false,
  };
}

export async function confirmSignUpCode(
  email: string,
  code: string
): Promise<boolean> {
  await startEmailLogin(email);
  await confirmOtpCode(code);
  return true;
}

export async function resendSignUp(email: string): Promise<void> {
  await startEmailLogin(email);
}

export async function confirmOtpCode(
  code: string
): Promise<AuthStatus & { nextStep?: string }> {
  await respondToCustomChallenge(code);
  return fetchAuthStatus();
}

export async function signOutUser(): Promise<void> {
  await signOut();
}

export async function tryRefreshAuthTokens(): Promise<boolean> {
  return refreshTokens();
}
